/**
 * How each AI agent presents itself, hard-coded in the frontend.
 *
 * Every string here is an English key that `t()` resolves, so an agent reads in the viewer's
 * language like the rest of the platform. The backend owns behaviour and keys; it deliberately
 * ships no prose. Adding an agent means adding its module server side and an entry here — a test
 * checks the two lists match.
 */
export const AGENT_PRESENTATION = {
  'visual-context-extractor': {
    color: '#0891b2',
    icon: [
      'M4 8.5V6a2 2 0 0 1 2-2h2.5',
      'M15.5 4H18a2 2 0 0 1 2 2v2.5',
      'M20 15.5V18a2 2 0 0 1-2 2h-2.5',
      'M8.5 20H6a2 2 0 0 1-2-2v-2.5',
      'M4 12h16',
    ],
    name: 'Visual context extraction',
    tagline: 'Reads what is written and visible on the picture',
    description: 'Extracts the readable commercial content of a picture: products, brands, categories, fixtures and POS material, with a focus on labels and prices. The reading profile is chosen question by question.',
    outputs: [
      'A factual summary of the scene',
      'Observed brands, controlled categories and tags',
      'Structured products, references and prices (label profile)',
      'OCR text and reading limitations',
    ],
    goodFor: [
      'Reading the prices and references displayed on a shelf',
      'Listing the fixtures and POS material in place',
      'Describing a mass display or an end cap',
    ],
    notFor: [
      'Judging whether an implementation meets an expectation — that is the compliance agent',
    ],
  },
  'auto-hide-client-photo': {
    color: '#b45309',
    icon: [
      'M3 3l18 18',
      'M10.6 5.1A9.9 9.9 0 0 1 12 5c5 0 9 4.5 9.8 6.6a1 1 0 0 1 0 .8 13 13 0 0 1-2.3 3.3',
      'M6.6 6.7A13 13 0 0 0 2.2 11.6a1 1 0 0 0 0 .8C3 14.5 7 19 12 19a10 10 0 0 0 4.3-1',
      'M9.9 9.9a3 3 0 0 0 4.2 4.2',
    ],
    name: 'Auto-hide from clients',
    tagline: 'Takes unusable and internal photos out of the client view',
    description: 'Screens each picture against the rules you tick, and hides the ones that should never reach a client: blurry or badly framed shots, internal photos, pictures unrelated to the question. Hidden means flagged admin-only, with the reason kept on the analysis.',
    outputs: [
      'A hide-or-keep decision with the rule that triggered it',
      'The visual evidence behind each rule',
      'A confidence score per rule, against your threshold',
    ],
    goodFor: [
      'Keeping blurry or unusable shots out of a client gallery',
      'Filtering internal photos: arrival selfies, visit prep sheets, stockroom shots',
      'Screening a programme whose photos reach clients directly',
    ],
    notFor: [
      'Making a photo visible again — it only ever hides, never reveals',
      'Reviewing a decision an administrator already took by hand',
      'Judging whether an implementation meets an expectation — that is the compliance agent',
    ],
  },
  compliance: {
    color: '#7c3aed',
    icon: [
      'M9 4.5h6',
      'M8.5 6H6.5A1.5 1.5 0 0 0 5 7.5v11A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5v-11A1.5 1.5 0 0 0 17.5 6h-2',
      'M9.5 3h5a.5.5 0 0 1 .5.5v1.5a.5.5 0 0 1-.5.5h-5a.5.5 0 0 1-.5-.5v-1.5a.5.5 0 0 1 .5-.5Z',
      'm8.5 13.5 2.2 2.2 4.8-4.8',
    ],
    name: 'Compliance / planogram',
    tagline: 'Grades a picture against a criteria grid',
    description: 'Grades a picture against a compliance grid written by the administrator: every criterion is graded with its visual evidence, and the overall verdict is derived from them.',
    outputs: [
      'A compliant / non-compliant / undetermined verdict, computed rather than guessed',
      'A status and visual evidence for every criterion in the grid',
      'The list of unmet blocking criteria',
      'A confidence derived from what the picture allowed to verify',
    ],
    goodFor: [
      'Checking a planogram or an expected implementation',
      'Verifying that a POS item or a brand header is in place',
      'Measuring a compliance rate per store or per campaign',
    ],
    notFor: [
      'Reading prices or references — that is the extraction agent',
    ],
  },
};

const FALLBACK = {
  color: '#64748b',
  icon: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z', 'M12 8v4', 'M12 16h.01'],
  name: 'Unknown agent',
  tagline: 'This agent needs a more recent version of the interface.',
  description: '',
  outputs: [],
  goodFor: [],
  notFor: [],
};

/** Presentation for an agent key, falling back rather than breaking on one this build predates. */
export const agentPresentation = (agentKey) => AGENT_PRESENTATION[agentKey] || { ...FALLBACK, key: agentKey };

export const AGENT_KEYS = Object.keys(AGENT_PRESENTATION);
